// Le Menu — la touche ☰ de la barre du bas, comme sur Facebook.
//
// Troisième écran migré. Il apporte deux choses que les précédents n'avaient
// pas : un compteur alimenté par DEUX abonnements à la fois, et des actions qui
// ouvrent une fenêtre modale.

import { useState } from "react";
import Store from "../../store.js";
import { APP } from "../../config.js";
import { modale, confirmer } from "../../ui.js";
import { estModerateur } from "../../views/moderation.js";
import Avatar from "../Avatar.jsx";
import Icone from "../Icone.jsx";
import { useAbonnement } from "../hooks.js";

// Les grandes destinations, en grille — comme les tuiles de Facebook.
const RACCOURCIS = [
  { id: "groupes", icone: "membres", label: "Groupes", detail: "Prière, départements, jeunesse", href: "#/groupes" },
  { id: "pages", icone: "drapeau", label: "Pages", detail: "Églises, ministères, chorales", href: "#/pages" },
  { id: "evenements", icone: "alertes", label: "Événements", detail: "Cultes, veillées, répétitions", href: "#/evenements" },
  { id: "membres", icone: "profil", label: "Membres", detail: "Annuaire et amitiés", href: "#/membres" },
  { id: "reels", icone: "reels", label: "Reels", detail: "Toutes les vidéos", href: "#/reels" },
  { id: "messages", icone: "messages", label: "Messages", detail: "Vos conversations", href: "#/messages" },
  { id: "notifications", icone: "alertes", label: "Notifications", detail: "Ce qui vous concerne", href: "#/notifications" },
];

const REGLES = [
  /* ⚠️ EN TÊTE, ET C'EST VOULU. Un membre qui lit ceci depuis le SITE n'a
     souvent pas l'application — il ne sait même pas qu'elle existe. C'est le
     seul endroit du compte où on peut le lui dire sans l'importuner.
     Ceux qui sont déjà dans l'application y verront une ligne inutile : c'est
     le prix, et il est petit devant un membre qui rate les notifications faute
     de savoir qu'il pouvait les avoir. */
  { href: "application.html", icone: "envoyer", label: "Installer l'application Android", externe: true },
  { href: "#/sponsoring", icone: "commenter", label: "Politique de sponsoring" },
  { href: "conditions.html", icone: "verset", label: "Conditions d'utilisation", externe: true },
  { href: "confidentialite.html", icone: "options", label: "Politique de confidentialité", externe: true },
];

/**
 * La tuile Modération et sa pastille.
 *
 * Isolée dans son propre composant pour une raison qui compte : ses deux
 * abonnements ne s'installent QUE si le membre est modérateur. En mode local,
 * rien ne filtre les signalements côté serveur — un simple membre verrait une
 * pastille pour un travail qui ne le regarde pas.
 */
function TuileModeration() {
  const signalements = useAbonnement((cb) => Store.reports.subscribe(cb), []);
  const baptemes = useAbonnement((cb) => Store.moderation.subscribeAValider(cb), []);
  const n = signalements.filter((x) => !x.traite).length + baptemes.length;

  return (
    <a className="menu-tuile menu-tuile--moderation" href="#/moderation">
      <Icone nom="options" taille={26} />
      <strong>Modération</strong>
      <span className="petit texte-doux">Signalements et baptêmes</span>
      {n > 0 && <span className="compteur">{n}</span>}
    </a>
  );
}

export default function Menu({ ctx }) {
  const [sortieEnCours, setSortieEnCours] = useState(false);
  const moderateur = estModerateur(ctx.moi);

  const ouvrirCharte = () =>
    modale({
      titre: "Charte de la communauté",
      // la charte vient de notre configuration, jamais d'un membre
      corps: `<ul class="politique-regles">${APP.charte.map((c) => `<li>${c}</li>`).join("")}</ul>`,
    });

  const seDeconnecter = async () => {
    if (sortieEnCours) return;
    setSortieEnCours(true);
    const oui = await confirmer(
      "Se déconnecter ?",
      "Vous devrez saisir votre mot de passe pour revenir.",
      "Se déconnecter"
    );
    if (oui) {
      await Store.auth.signOut();
      location.hash = "#/fil";
    } else {
      setSortieEnCours(false);
    }
  };

  return (
    <div className="colonne">
      <div className="entete-page">
        <h1 className="titre-page">Menu</h1>
      </div>

      <a className="carte menu-moi" href="#/profil">
        <Avatar membre={ctx.moi} taille={60} anneau={ctx.avecStatut?.has(ctx.moi.uid)} />
        <span>
          <strong>{ctx.moi.nom}</strong>
          <span className="petit texte-doux">Voir votre profil</span>
        </span>
      </a>

      <div className="menu-grille">
        {RACCOURCIS.map((r) => (
          <a className="menu-tuile" href={r.href} key={r.id}>
            <Icone nom={r.icone} taille={26} />
            <strong>{r.label}</strong>
            <span className="petit texte-doux">{r.detail}</span>
          </a>
        ))}
        {moderateur && <TuileModeration />}
      </div>

      <a className="carte menu-entree" href="#/parametres">
        <Icone nom="options" taille={22} />
        <span>
          <strong>Paramètres et confidentialité</strong>
          <span className="petit texte-doux">
            Compte, qui voit quoi, notifications, mot de passe, vos données
          </span>
        </span>
      </a>

      <section className="carte">
        <h2 className="profil__titre">Règles de la communauté</h2>
        <div className="menu-liste">
          <a className="menu-item" href={REGLES[0].href}>
            <Icone nom={REGLES[0].icone} taille={20} /> {REGLES[0].label}
          </a>
          <button className="menu-item" onClick={ouvrirCharte}>
            <Icone nom="verset" taille={20} /> Charte de la communauté
          </button>
          {REGLES.slice(1).map((r) => (
            <a className="menu-item" href={r.href} target="_blank" rel="noopener" key={r.href}>
              <Icone nom={r.icone} taille={20} /> {r.label}
            </a>
          ))}
        </div>
      </section>

      <button className="btn btn--gris btn--bloc" onClick={seDeconnecter} disabled={sortieEnCours}>
        Se déconnecter
      </button>
      <p className="petit texte-doux" style={{ textAlign: "center" }}>
        {APP.nom} — {APP.slogan}
      </p>
    </div>
  );
}
